"use client";

import Link from "next/link";
import { useState, type ReactElement } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost";
type ButtonColor = "gray" | "white" | "black";

type BaseButtonProps = {
  text: string;
  variant?: ButtonVariant;
  version?: ButtonColor;
  isRounded?: boolean;
  hasIcon?: boolean;
  watchIcon?: boolean;
  customIcon?: ReactElement;
  target?: string;
  textSizeBtnClass?: string;
  videoUrl?: string;
};

type ButtonWithUrl = BaseButtonProps & {
  url: string;
  onClick?: never;
};

type ButtonWithOnClick = BaseButtonProps & {
  url?: string;
  onClick: () => void;
};

type ButtonWithVideo = BaseButtonProps & {
  videoUrl: string;
  url?: never;
  onClick?: never;
};

type CustomButtonProps = ButtonWithUrl | ButtonWithOnClick | ButtonWithVideo;

const BASE_CLASSES =
  "flex h-[38px] items-center justify-center border border-solid flex-shrink-0 font-normal";

const BUTTON_STYLES: Record<ButtonVariant, Record<ButtonColor, string>> = {
  primary: {
    gray: "bg-btnGray text-white border-btnGray hover:bg-btnGray/90",
    white: "bg-white text-brand border-white",
    black: "bg-brand text-white border-brand hover:bg-brand-hover",
  },
  secondary: {
    gray: "bg-transparent text-outlineText border-outlineBorder hover:text-white hover:border-white",
    white: "bg-white text-outlineText border-outlineBorder",
    black:
      "bg-white text-outlineText border-outlineBorder hover:border-brand hover:bg-brand-hover hover:text-white",
  },
  ghost: {
    gray: "bg-transparent text-textGray border-transparent hover:text-white",
    white: "bg-transparent text-white border-transparent",
    black:
      "bg-transparent text-outlineText border-transparent hover:bg-brand hover:text-white",
  },
};

const CustomButton = ({
  text,
  url = "/",
  onClick,
  videoUrl,
  variant = "primary",
  version = "gray",
  isRounded,
  hasIcon,
  watchIcon,
  customIcon,
  target,
  textSizeBtnClass = "text-[14px]",
}: CustomButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const spacingClasses =
    hasIcon || watchIcon || customIcon ? "gap-2 px-6 pr-3" : "px-3 lg:px-4";

  const roundedClasses = isRounded ? "rounded-[50px]" : "rounded-[4px]";

  const buttonClasses = `
    ${BASE_CLASSES}
    ${BUTTON_STYLES[variant][version]}
    ${roundedClasses}
    ${textSizeBtnClass}
    ${spacingClasses}
  `;

  const content = (
    <>
      <span>{text}</span>

      {customIcon && <span>{customIcon}</span>}

      {watchIcon && (
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M6 12.5V9.83C6 6.52 8.35 5.16 11.22 6.82L15.84 9.5C18.71 11.16 18.71 13.87 15.84 15.53L11.22 18.21C8.35 19.84 6 18.49 6 15.17V12.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}

      {hasIcon && (
        <span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 21L16 12L8 3"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </>
  );

  if (videoUrl) {
    return (
      <>
        <button className={buttonClasses} onClick={() => setIsModalOpen(true)}>
          {content}
        </button>

        {isModalOpen && (
          <>
            <div
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 z-[99999] bg-black/50"
            />
            <div className="fixed left-1/2 top-1/2 z-[999999] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 px-4">
              <div className="relative rounded-lg bg-white shadow">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute right-2 top-2 rounded-full bg-black p-2 text-white hover:bg-gray-800"
                  aria-label="Close video"
                >
                  ✕
                </button>
                <iframe
                  width="100%"
                  height="500"
                  src={videoUrl}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  if (onClick) {
    return (
      <button className={buttonClasses} onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <Link
      href={url}
      target={target === "none" ? undefined : target}
      className={buttonClasses}
    >
      {content}
    </Link>
  );
};

export default CustomButton;
